import { SERVICE_PROVIDERS } from 'modules/graphql/types';
import { z } from 'zod';
import { AuthType, ESP_ACCOUNT_PAYLOAD_FIELDS } from '../../types';
import { refineApiEndpoint, refineIterableSmsFields } from './refiners';
import {
  DYN,
  SPARKPOST,
  MAILGUN,
  SENDGRID,
  TELNYX,
  TWILIO,
  ITERABLE_SMS,
  CUSTOM_EMAIL,
  ConfigurableSendingPlatforms,
  WHATS_APP,
} from '../../constants';

const { NAME, API_KEY, ESP, BINDING, API_ENDPOINT, SIGNING_SECRET } = ESP_ACCOUNT_PAYLOAD_FIELDS;
const { CLIENT_ID, CLIENT_SECRET, TOKEN_ENDPOINT, AUTH_TYPE, CUSTOM_HEADERS } =
  ESP_ACCOUNT_PAYLOAD_FIELDS;

export const genericCreateAccountSchema = z.object({
  [NAME]: z.string().min(1, { message: 'Enter a name' }),
  [API_KEY]: z.string().min(1, { message: 'Enter an API key' }),
  [ESP]: z.nativeEnum(SERVICE_PROVIDERS),
});

export const sparkpostCreateAccountSchema = genericCreateAccountSchema
  .extend({
    [BINDING]: z.string().min(1, { message: 'Enter an IP pool' }),
    [API_ENDPOINT]: z.string(),
  })
  .superRefine(refineApiEndpoint);

export const telnyxCreateAccountSchema = genericCreateAccountSchema.extend({
  [SIGNING_SECRET]: z.string().min(1, { message: 'Enter a signing secret' }),
});

export const twilioCreateAccountSchema = z.object({
  [NAME]: z.string().min(1, { message: 'Enter an API key SID' }),
  [API_KEY]: z.string().min(1, { message: 'Enter an API key secret' }),
  [ESP]: z.nativeEnum(SERVICE_PROVIDERS),
  [API_ENDPOINT]: z.string().min(1, { message: 'Enter an Account SID' }),
});

export const iterableSmsCreateAccountSchema = z
  .object({
    [NAME]: z.string(),
    [API_KEY]: z.string(),
    [ESP]: z.nativeEnum(SERVICE_PROVIDERS),
    [API_ENDPOINT]: z.string(),
  })
  .superRefine(refineIterableSmsFields);

export const customEmailCreateAccountSchema = z.object({
  [ESP]: z.nativeEnum(SERVICE_PROVIDERS),
  [NAME]: z.string().min(1, { message: 'Enter a name' }),
  [API_ENDPOINT]: z.string().min(1, { message: 'Enter an endpoint' }).url(),
  [AUTH_TYPE]: z.nativeEnum(AuthType),
  [CLIENT_ID]: z.string().min(1, { message: 'Enter a Client ID' }),
  [CLIENT_SECRET]: z.string().min(1, { message: 'Enter a Client secret' }),
  [TOKEN_ENDPOINT]: z.string().min(1, { message: 'Enter a Token URL' }).url(),
  [CUSTOM_HEADERS]: z.array(z.record(z.string().min(1, { message: 'Enter a value' }))),
});

type SchemaType =
  | typeof genericCreateAccountSchema
  | typeof sparkpostCreateAccountSchema
  | typeof twilioCreateAccountSchema
  | typeof telnyxCreateAccountSchema
  | typeof iterableSmsCreateAccountSchema
  | typeof customEmailCreateAccountSchema;

export const CREATE_SCHEMAS: Record<ConfigurableSendingPlatforms, SchemaType> = {
  [DYN]: genericCreateAccountSchema,
  [SPARKPOST]: sparkpostCreateAccountSchema,
  [MAILGUN]: genericCreateAccountSchema,
  [SENDGRID]: genericCreateAccountSchema,
  [TELNYX]: telnyxCreateAccountSchema,
  [TWILIO]: twilioCreateAccountSchema,
  [ITERABLE_SMS]: iterableSmsCreateAccountSchema,
  [CUSTOM_EMAIL]: customEmailCreateAccountSchema,
  [WHATS_APP]: genericCreateAccountSchema,
};
